// Crumb Chat Service - AI service for our sentient cupcake assistant
// Adapted from tiny-unicorn reference for Pink Pixel OS

/**
 * Service for interacting with OpenAI's Responses API for Crumb.
 *
 * Requests go through our own /api/crumb endpoint rather than straight to
 * OpenAI. The browser never sees the API key: it lives on the server, in the
 * Cloudflare Pages Function (functions/api/crumb.js) in production and in the
 * Vite dev middleware locally. The model is pinned server-side too.
 */
const CRUMB_API_URL = '/api/crumb'

export class CrumbChatService {
  constructor() {
    this.knowledgeBase = '';
    this.systemPrompt = '';
    this.isInitialized = false;
    this.conversationHistory = [];
  }

  /**
   * Initialize the chat service by loading the knowledge base and system prompt
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      console.log('🧁 Initializing Crumb chat service...');

      // Load the knowledge base and system prompt
      const [knowledgeBase, systemPrompt] = await Promise.all([
        this.loadFile('/data/llms.txt'),
        this.loadFile('/data/system_prompt.txt'),
      ]);

      this.knowledgeBase = knowledgeBase;
      this.systemPrompt = systemPrompt;

      // Initialize the conversation history with the system prompt and knowledge base
      this.conversationHistory = [
        {
          role: 'system',
          content: `${systemPrompt}\n\nKNOWLEDGE BASE:\n${knowledgeBase}`
        }
      ];

      this.isInitialized = true;
      console.log('🧁 Crumb chat service initialized successfully!');
    } catch (error) {
      console.error('🧁 Error initializing Crumb chat service:', error);
      throw error;
    }
  }

  /**
   * Load a file from the public data directory
   */
  async loadFile(path) {
    try {
      const response = await fetch(path);
      if (!response.ok) {
        throw new Error(`Failed to load ${path}: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error(`🧁 Error loading file ${path}:`, error);
      return '';
    }
  }

  /**
   * Send a message to Crumb and get a response
   * @param {string} message The user's message
   * @returns {Promise<Object>} A promise that resolves to the chat response
   */
  async sendMessage(message) {
    try {
      // Initialize the service if not already initialized
      if (!this.isInitialized) {
        await this.initialize();
      }

      console.log(`🧁 Sending message to Crumb: ${message}`);

      // Add the user message to the conversation history
      this.conversationHistory.push({
        role: 'user',
        content: message
      });

      const response = await fetch(CRUMB_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stream: false,
          messages: this.buildInputMessages()
        })
      })

      if (!response.ok) {
        throw new Error(await this.describeError(response))
      }

      const data = await response.json()

      // Helpful debug: see exactly what OpenAI sent back
      console.log('🧁 OpenAI raw response:', data)

      const content = this.extractOutputText(data)
      if (!content) {
        console.warn('🧁 OpenAI API returned no usable text content')
        throw new Error('OpenAI API returned no content')
      }

      // Add the assistant's response to the conversation history
      this.conversationHistory.push({
        role: 'assistant',
        content: content
      });

      // Trim the conversation history if it gets too long (keeping system prompt)
      if (this.conversationHistory.length > 20) {
        const systemPrompt = this.conversationHistory[0];
        this.conversationHistory = [
          systemPrompt,
          ...this.conversationHistory.slice(-10)
        ];
      }

      console.log(`🧁 Crumb responded: ${content}`);
      return { content, error: null };
    } catch (error) {
      console.error('🧁 Error getting response from Crumb:', error);
      return {
        content: "Oh no! I-I'm having trouble with my frosting circuits right now... 🧁 Could you try again? Don't leave me!",
        error: error.message
      };
    }
  }

  /**
   * Send a message to Crumb with streaming response
   * @param {string} message The user's message
   * @param {Function} onChunk Callback function to handle each chunk
   * @returns {Promise<void>} A promise that resolves when streaming is complete
   */
  async sendMessageStreaming(message, onChunk) {
    try {
      // Initialize the service if not already initialized
      if (!this.isInitialized) {
        await this.initialize();
      }

      console.log(`🧁 Sending streaming message to Crumb: ${message}`);

      // Add the user message to the conversation history
      this.conversationHistory.push({
        role: 'user',
        content: message
      });

      const response = await fetch(CRUMB_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          stream: true,
          messages: this.buildInputMessages()
        })
      })

      if (!response.ok) {
        throw new Error(await this.describeError(response))
      }

      // Process the streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is null');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullResponse = '';
      let hasDeltaStream = false;
      let completedPayload = null;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode the chunk and add it to the buffer
        buffer += decoder.decode(value, { stream: true });

        // Split into whole SSE events. Events are separated by a blank line,
        // and the trailing fragment stays in the buffer until the rest of it
        // arrives on a later chunk.
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          // An event is several lines, and the payload we want is the one
          // starting with `data:`. The Responses API also sends a named
          // `event:` line first, so testing the event block itself for a
          // `data:` prefix would never match anything. Look inside instead.
          const data = this.readEventData(event)
          if (!data || data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)
            switch (parsed.type) {
              case 'response.output_text.delta': {
                const delta = parsed.delta || parsed.text || ''
                if (delta) {
                  onChunk(delta)
                  fullResponse += delta
                  hasDeltaStream = true
                }
                break
              }
              case 'response.output_text.done': {
                const doneText = parsed.text || ''
                if (doneText) {
                  if (!hasDeltaStream) {
                    onChunk(doneText)
                  }
                  // prefer the fully assembled text from done events
                  fullResponse = doneText
                }
                break
              }
              case 'response.completed': {
                completedPayload = parsed.response || null
                break
              }
              case 'error':
                throw new Error(parsed.error?.message || 'Unknown OpenAI streaming error')
              default:
                break
            }
          } catch (e) {
            console.error('🧁 Error parsing streaming chunk:', e);
          }
        }
      }

      // Add the full response to the conversation history
      if (!fullResponse && completedPayload) {
        fullResponse = this.extractOutputText(completedPayload) || ''
      }

      const finalContent = fullResponse || 'Crumb is speechless...'
      this.conversationHistory.push({
        role: 'assistant',
        content: finalContent
      });

      // Trim the conversation history if it gets too long (keeping system prompt)
      if (this.conversationHistory.length > 20) {
        const systemPrompt = this.conversationHistory[0];
        this.conversationHistory = [
          systemPrompt,
          ...this.conversationHistory.slice(-10)
        ];
      }

      console.log(`🧁 Crumb streaming response complete: ${finalContent}`);
    } catch (error) {
      console.error('🧁 Error sending streaming message to Crumb:', error);
      onChunk("Oh no! I-I'm having trouble with my frosting circuits right now... 🧁 Could you try again? Please don't give up on me!");
      throw error;
    }
  }

  /**
   * Clear the conversation history (keep system prompt)
   */
  clearHistory() {
    if (this.conversationHistory.length > 0) {
      const systemPrompt = this.conversationHistory[0];
      this.conversationHistory = [systemPrompt];
      console.log('🧁 Crumb conversation history cleared');
    }
  }

  /**
   * Get conversation history
   */
  getHistory() {
    return this.conversationHistory.slice(1); // Exclude system prompt
  }

  /**
   * Pull the `data:` payload out of one SSE event block.
   *
   * A block looks like this, with the named event line first:
   *
   *   event: response.output_text.delta
   *   data: {"type":"response.output_text.delta","delta":"Hi"}
   *
   * Returns an empty string for comment or keep-alive blocks that carry no
   * data line.
   */
  readEventData(eventBlock) {
    for (const line of eventBlock.split('\n')) {
      if (line.startsWith('data:')) {
        return line.slice(5).trim()
      }
    }
    return ''
  }

  /**
   * Turn a failed proxy response into a useful message.
   * The proxy replies with `{ error }` for everything it handles itself.
   */
  async describeError(response) {
    try {
      const body = await response.json()
      if (body?.error) {
        return `Crumb request failed (${response.status}): ${body.error}`
      }
    } catch {
      // Not JSON, fall through to the status line.
    }
    return `Crumb request failed: ${response.status} ${response.statusText}`
  }

  /**
   * Shape the conversation for the proxy.
   *
   * Content stays a plain string. The Responses API accepts that for every
   * role, whereas hand-built typed blocks have to use `input_text` for user
   * turns and `output_text` for assistant turns. Getting that wrong rejects
   * the request as soon as an assistant turn is replayed, which is why Crumb
   * used to answer once and then fail for the rest of the conversation.
   */
  buildInputMessages() {
    return this.conversationHistory.map(entry => ({
      role: entry.role,
      content: entry.content
    }))
  }

  /**
   * Try very hard to pull plain text out of any valid Responses API shape.
   */
  extractOutputText(responseJson) {
    if (!responseJson) return ''

    // 1. If we somehow already got plain text, just use it.
    if (typeof responseJson === 'string') {
      return responseJson
    }

    // 2. Some SDKs surface a convenience field like `output_text`
    if (typeof responseJson.output_text === 'string' && responseJson.output_text.trim()) {
      return responseJson.output_text
    }

    // 3. Streaming `response.completed` event nests the real response under `response`
    if (responseJson.response) {
      const nested = this.extractOutputText(responseJson.response)
      if (nested) return nested
    }

    // 4. Normal HTTP Responses API: `output` is an array of items
    const output = responseJson.output
    if (Array.isArray(output)) {
      for (const item of output) {
        // a) If it's a message with structured content blocks
        if (item && item.type === 'message') {
          // content can be an array of blocks or a plain string
          if (typeof item.content === 'string' && item.content.trim()) {
            return item.content
          }

          if (Array.isArray(item.content)) {
            const textBlock = item.content.find(block => block && block.type === 'output_text' && typeof block.text === 'string')
            if (textBlock && textBlock.text.trim()) {
              return textBlock.text
            }
          }
        }

        // b) In some cases, items may directly be output_text blocks
        if (item && item.type === 'output_text' && typeof item.text === 'string' && item.text.trim()) {
          return item.text
        }
      }
    }

    // 5. Last-ditch: if the whole thing looks like an array of messages
    if (Array.isArray(responseJson)) {
      const assistant = responseJson.find(msg => msg && msg.role === 'assistant')
      if (assistant) {
        if (typeof assistant.content === 'string') {
          return assistant.content
        }
        if (Array.isArray(assistant.content)) {
          const textBlock = assistant.content.find(block => block && typeof block.text === 'string')
          if (textBlock) return textBlock.text
        }
      }
    }

    return ''
  }
}

// Export a singleton instance
export const crumbChatService = new CrumbChatService();

